import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { of } from 'rxjs';
import { catchError, map, mergeMap, tap } from 'rxjs/operators';
import { Router } from '@angular/router';
import { ItemService } from 'app/modules/masters/item/item.service';
import { checkItemExists, checkItemExistsSuccess, checkItemExistsFailure } from './item.actions';
import {
  loadItems,
  loadItemsSuccess,
  loadItemsFailure,
  createItem,
  createItemSuccess,
  createItemFailure,
  searchItems,
  searchItemsFailure,
  searchItemsSuccess,
  updateItem,
  updateItemSuccess,
  updateItemFailure,
  deleteItem,
  deleteItemSuccess,
  deleteItemFailure,
} from './item.actions';
import { MatSnackBar } from '@angular/material/snack-bar';
import { loadItem, loadItemSuccess, loadItemFailure } from './item.actions';

@Injectable()
export class ItemEffects {
  constructor(
    private actions$: Actions,
    private itemService: ItemService,
    private router: Router,
    private snackBar: MatSnackBar,
  ) {}

  // Load all items
  loadItems$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadItems),
      mergeMap(() =>
        this.itemService.listItems().pipe(
          map((items) => loadItemsSuccess({ items })),
          catchError((error) =>
            of(
              loadItemsFailure({
                error: error.message || 'Failed to load items',
              }),
            ),
          ),
        ),
      ),
    ),
  );
  // Load single item by ID
  loadItem$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadItem),
      mergeMap((action) =>
        this.itemService.getItemById(action.id).pipe(
          map((item) => loadItemSuccess({ item })),
          catchError((error) =>
            of(
              loadItemFailure({
                error: error.message || 'Failed to load item',
              }),
            ),
          ),
        ),
      ),
    ),
  );
  // Create new item
  createItem$ = createEffect(() =>
    this.actions$.pipe(
      ofType(createItem),
      mergeMap(({ item }) =>
        this.itemService.createItem(item).pipe(
          map((createdItem) => createItemSuccess({ item: createdItem })),
          catchError((error) =>
            of(
              createItemFailure({
                error: error.message || 'Failed to create item',
              }),
            ),
          ),
        ),
      ),
    ),
  );

  // On successful creation, reload items and navigate
  createItemSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(createItemSuccess),
      tap(() => {
        this.snackBar.open('Item created successfully', 'Close', {
          duration: 3000,
        });
        this.router.navigateByUrl('/item/list');
      }),

      map(() => loadItems()), // Reload items list
    ),
  );

  // Search Item
  searchItems$ = createEffect(() =>
    this.actions$.pipe(
      ofType(searchItems),
      mergeMap((action) =>
        this.itemService
          .searchItems(action.query, action.searchType, action.skip ?? 0, action.take ?? 10)
          .pipe(
            map((response) =>
              searchItemsSuccess({
                items: response.items,
                total: response.total,
                rowIndex: action.rowIndex,
              }),
            ),
            catchError((error) => of(searchItemsFailure({ error }))),
          ),
      ),
    ),
  );

  //Update existing item
  updateItem$ = createEffect(() =>
    this.actions$.pipe(
      ofType(updateItem),
      mergeMap(({ id, item }) =>
        this.itemService.updateItem(id, item).pipe(
          map((updatedItem) => updateItemSuccess({ item: updatedItem })),
          catchError((error) =>
            of(
              updateItemFailure({
                error: error.message || 'Failed to update item',
              }),
            ),
          ),
        ),
      ),
    ),
  );

  //On successful update, reload items and navigate
  updateItemSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(updateItemSuccess),
      tap(() => {
        this.snackBar.open('Item updated successfully', 'Close', {
          duration: 3000,
        });
        this.router.navigate(['/item/list']);
      }),
      map(() => loadItems()), // Reload items list
    ),
  );

  //Delete item
  deleteItem$ = createEffect(() =>
    this.actions$.pipe(
      ofType(deleteItem),
      mergeMap(({ id }) =>
        this.itemService.deleteItem(id).pipe(
          map(() => deleteItemSuccess({ id })),
          catchError((error) =>
            of(
              deleteItemFailure({
                error: error.message || 'Failed to delete item',
              }),
            ),
          ),
        ),
      ),
    ),
  );

  //On successful deletion, reload items and show notification
  deleteItemSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(deleteItemSuccess),
      tap(() => {
        this.snackBar.open('Item deleted successfully', 'Close', {
          duration: 3000,
        });
      }),
      map(() => loadItems()), // Reload items list
    ),
  );

  // Handle all failures
  handleFailure$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(loadItemsFailure, createItemFailure, updateItemFailure, deleteItemFailure),
        tap(({ error }) => {
          this.snackBar.open(`Error: ${error}`, 'Close', {
            duration: 5000,
            panelClass: ['error-snackbar'],
          });
        }),
      ),
    { dispatch: false },
  );

  checkItemExists$ = createEffect(() =>
    this.actions$.pipe(
      ofType(checkItemExists),
      mergeMap(({ name, excludeId }) =>
        this.itemService.checkItemNameExists(name, excludeId).pipe(
          map((exists) => checkItemExistsSuccess({ exists })),
          catchError((error) =>
            of(
              checkItemExistsFailure({
                error: error.message || 'Failed to check item existence',
              }),
            ),
          ),
        ),
      ),
    ),
  );
}
